import { api } from './baseApi';
import type { User, LoginCredentials, ApiResponse } from '@/types';

export const authApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Login endpoint
    login: builder.mutation<
      ApiResponse<{ user: User; tokens: { accessToken: string; refreshToken: string } }>,
      LoginCredentials
    >({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Auth'],
    }),

    // Get current user profile
    me: builder.query<ApiResponse<User>, void>({
      query: () => '/auth/me',
      providesTags: ['Auth'],
    }),

    // Logout endpoint
    logout: builder.mutation<ApiResponse<void>, void>({
      query: () => ({
        url: '/auth/logout',
        method: 'POST',
      }),
      invalidatesTags: ['Auth'],
    }),

    // Change password
    changePassword: builder.mutation<
      ApiResponse<void>,
      { currentPassword: string; newPassword: string }
    >({
      query: (passwords) => ({
        url: '/auth/change-password',
        method: 'POST',
        body: passwords,
      }),
    }),

    // Refresh token
    refreshToken: builder.mutation<
      ApiResponse<{ tokens: { accessToken: string; refreshToken: string } }>,
      void
    >({
      query: () => ({
        url: '/auth/refresh',
        method: 'POST',
      }),
    }),

    // Verify password (for fullscreen exit)
    verifyPassword: builder.mutation<
      ApiResponse<{ valid: boolean }>,
      { password: string }
    >({
      query: (data) => ({
        url: '/auth/verify-password',
        method: 'POST',
        body: data,
      }),
    }),

    // Switch store
    switchStore: builder.mutation<
      ApiResponse<{
        tokens: { accessToken: string; refreshToken: string };
        currentStore: { id: string; name: string };
        currentOutlet: { id: string; name: string } | null;
      }>,
      { storeId: string }
    >({
      query: (data) => ({
        url: '/auth/switch-store',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Auth'],
    }),

    // Switch outlet
    switchOutlet: builder.mutation<
      ApiResponse<{
        tokens: { accessToken: string; refreshToken: string };
        currentOutlet: { id: string; name: string };
      }>,
      { outletId: string }
    >({
      query: (data) => ({
        url: '/auth/switch-outlet',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Auth'],
    }),

    // Get user context
    getContext: builder.query<
      ApiResponse<{
        currentStore: { id: string; name: string; isActive: boolean } | null;
        currentOutlet: { id: string; name: string; storeId: string; isActive: boolean } | null;
        stores: Array<{ id: string; name: string; role: string; isActive: boolean }>;
        outlets: Array<{ id: string; name: string; storeId: string; isActive: boolean }>;
      }>,
      void
    >({
      query: () => '/auth/context',
      providesTags: ['Auth'],
    }),
  }),
});

export const {
  useLoginMutation,
  useMeQuery,
  useLogoutMutation,
  useChangePasswordMutation,
  useRefreshTokenMutation,
  useVerifyPasswordMutation,
  useSwitchStoreMutation,
  useSwitchOutletMutation,
  useGetContextQuery,
} = authApi;